import { useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import {
  EditTaskItemDueDateDto,
  FetchError,
  GetUserTaskItemsDto,
  optimisticallyUpdateQueryData,
  queryKeys,
  TaskItemDetailDto,
  useAccessToken,
  useTaskItemMutation,
} from "src/http";

type Variables = {
  taskItemGuid: string;
  dueDate: string | null;
  teamSlug?: string;
  ownerUserGuids: string[];
};

const useEditTaskItemDueDateRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editTaskItemDueDateRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditTaskItemDueDateDto = {
      dueDate: variables.dueDate,
    };

    const response = await fetch(`${configuration.apiRootUrl}/task-items/${variables.taskItemGuid}/due-date`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editTaskItemDueDateRequest };
};

const useEditTaskItemDueDate = () => {
  const { editTaskItemDueDateRequest } = useEditTaskItemDueDateRequest();
  const queryClient = useQueryClient();

  return useTaskItemMutation({
    mutationFn: editTaskItemDueDateRequest,
    onMutate: async (variables) => {
      let cachedDetailData: TaskItemDetailDto | undefined;
      let cachedUserData: { [userGuid: string]: GetUserTaskItemsDto | undefined } = {};

      cachedDetailData = await optimisticallyUpdateQueryData<TaskItemDetailDto>(
        queryClient,
        queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid),
        () => ({
          dueDate: variables.dueDate,
        })
      );

      if (variables.ownerUserGuids) {
        variables.ownerUserGuids.forEach(async (userGuid) => {
          cachedUserData[userGuid] = await optimisticallyUpdateQueryData<GetUserTaskItemsDto>(
            queryClient,
            queryKeys.taskItems.getUserTaskItems(userGuid),
            (cachedData) => ({
              taskItems: cachedData.taskItems.map((taskItem) => {
                if (taskItem.guid === variables.taskItemGuid) {
                  return { ...taskItem, dueDate: variables.dueDate };
                } else {
                  return taskItem;
                }
              }),
            })
          );
        });
      }

      return {
        cachedDetailData,
        cachedUserData,
      };
    },
  });
};

export { useEditTaskItemDueDate };
